import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React, { useMemo } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, { FadeInDown, useReducedMotion } from 'react-native-reanimated';

import { WcHistory } from '@/components/competition/WcHistory';
import { FixtureRow } from '@/components/matches/FixtureRow';
import { FirstPredictNudge } from '@/components/onboarding/FirstPredictNudge';
import { LevelBar } from '@/components/profile/LevelBar';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useMyAlbum } from '@/features/games/album';
import { useAccaState } from '@/features/games/acca';
import { useMyLevel } from '@/features/games/levels';
import { useDailyCheckin, useDailyMissions, useWallet } from '@/features/games/rewards';
import { useMyScorePredictions } from '@/features/games/scorePredict';
import { useFixturesForDay } from '@/features/matches/hooks';
import { useAuth } from '@/lib/auth/AuthProvider';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { errorKey } from '@/lib/utils/errors';

type IoniconName = keyof typeof Ionicons.glyphMap;

/** The three states a daily task can be in. Drives the trailing CTA / check. */
type TaskState = 'todo' | 'done' | 'action';

interface DailyTask {
  key: string;
  icon: IoniconName;
  tint: string;
  title: string;
  body: string;
  state: TaskState;
  /** CTA label shown on a 'todo'/'action' task. */
  cta: string;
  onPress: () => void;
}

/**
 * One row of the daily checklist: an icon, a label + live status line, and a
 * trailing control — a green check when done, otherwise a compact CTA pill. The
 * whole row is tappable so the check itself is never the only target.
 */
function TaskRow({ task, index }: { task: DailyTask; index: number }) {
  const palette = useThemePalette();
  const reduce = useReducedMotion();
  const done = task.state === 'done';
  return (
    <Animated.View entering={reduce ? undefined : FadeInDown.delay(index * 60).springify()}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: done }}
        onPress={task.onPress}
        className="mx-4 mb-2.5 active:opacity-90"
      >
        <Card className={`flex-row items-center gap-3 px-4 py-3.5 ${done ? 'opacity-80' : ''}`}>
          <View
            className="h-10 w-10 items-center justify-center rounded-full"
            style={{ backgroundColor: done ? `${palette.win}1F` : `${task.tint}1F` }}
          >
            <Ionicons name={task.icon} size={19} color={done ? palette.win : task.tint} />
          </View>

          <View className="flex-1">
            <AppText
              variant="caption"
              weight="bold"
              className={done ? 'text-soft' : 'text-ink'}
            >
              {task.title}
            </AppText>
            <AppText variant="micro" className="mt-0.5 leading-4 text-muted" numberOfLines={2}>
              {task.body}
            </AppText>
          </View>

          {done ? (
            <View
              className="h-7 w-7 items-center justify-center rounded-full"
              style={{ backgroundColor: `${palette.win}26` }}
            >
              <Ionicons name="checkmark" size={16} color={palette.win} />
            </View>
          ) : (
            <View
              className="flex-row items-center gap-1 rounded-full px-3 py-1.5"
              style={{ backgroundColor: `${task.tint}1F` }}
            >
              <AppText variant="micro" weight="bold" style={{ color: task.tint }}>
                {task.cta}
              </AppText>
              <Ionicons name="arrow-forward" size={12} color={task.tint} />
            </View>
          )}
        </Card>
      </Pressable>
    </Animated.View>
  );
}

/** A tappable launchpad row that summarises one area + chevrons through to it. */
function LinkCard({
  icon,
  tint,
  title,
  body,
  onPress,
}: {
  icon: IoniconName;
  tint: string;
  title: string;
  body: string;
  onPress: () => void;
}) {
  const palette = useThemePalette();
  return (
    <Pressable accessibilityRole="button" onPress={onPress} className="active:opacity-80">
      <Card className="mx-4 mb-2.5 flex-row items-center gap-3 px-4 py-3.5">
        <View className="h-10 w-10 items-center justify-center rounded-full" style={{ backgroundColor: `${tint}22` }}>
          <Ionicons name={icon} size={19} color={tint} />
        </View>
        <View className="flex-1">
          <AppText variant="caption" weight="bold" className="text-ink">
            {title}
          </AppText>
          <AppText variant="micro" className="text-muted">
            {body}
          </AppText>
        </View>
        <Ionicons name="chevron-forward" size={18} color={palette.inactive} />
      </Card>
    </Pressable>
  );
}

export default function TodayScreen() {
  const { t } = useTranslation();
  const { numeralOptions, isRTL } = useLocale();
  const palette = useThemePalette();
  const router = useRouter();
  const { userId } = useAuth();
  const toast = useToast();

  const today = useMemo(() => new Date(), []);
  const fixturesQ = useFixturesForDay(today);
  const walletQ = useWallet();
  const levelQ = useMyLevel();
  const missionsQ = useDailyMissions();
  const predictionsQ = useMyScorePredictions();
  const albumQ = useMyAlbum();
  const accaQ = useAccaState();
  const checkin = useDailyCheckin();

  const fixtures = useMemo(() => fixturesQ.data ?? [], [fixturesQ.data]);
  const live = useMemo(() => fixtures.filter((f) => f.status === 'live'), [fixtures]);
  const ordered = useMemo(
    () =>
      [...fixtures].sort((a, b) => {
        // Live first (consistent comparator), then chronological by kickoff.
        const al = a.status === 'live' ? 0 : 1;
        const bl = b.status === 'live' ? 0 : 1;
        return al - bl || a.kickoff_at.localeCompare(b.kickoff_at);
      }),
    [fixtures],
  );

  const n = (v: number) => formatNumber(v, numeralOptions);
  const wallet = walletQ.data;

  // Today's score-prediction coverage: how many of today's fixtures are predicted.
  const predicted = useMemo(() => {
    const predictedIds = new Set((predictionsQ.data ?? []).map((p) => p.fixture_id));
    const made = fixtures.filter((f) => predictedIds.has(f.id)).length;
    return { made, total: fixtures.length };
  }, [fixtures, predictionsQ.data]);

  // A live or finished match today is the cheapest, surest PotM target.
  const potmFixture = useMemo(
    () => ordered.find((f) => f.status === 'live') ?? ordered.find((f) => f.status === 'finished'),
    [ordered],
  );

  const onCheckin = () =>
    checkin.mutate(undefined, {
      onSuccess: (res) => {
        if (!res.already_checked_in) {
          toast.show({
            message: t('rewards.claimed', {
              coins: formatNumber(res.reward, numeralOptions),
              streak: formatNumber(res.streak, numeralOptions),
            }),
            tone: 'success',
          });
        }
      },
      onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
    });

  // ---- Build the checklist. Each task self-hides when its data source is absent.
  const tasks: DailyTask[] = [];

  if (userId) {
    // 1) Daily check-in / streak claim.
    const checkedIn = wallet?.checked_in_today ?? false;
    tasks.push({
      key: 'checkin',
      icon: 'flame',
      tint: palette.volt,
      title: t('today.checkinTitle'),
      body: checkedIn
        ? t('today.checkinDone', { count: n(wallet?.streak ?? 0) })
        : t('today.checkinClaim'),
      state: checkedIn ? 'done' : 'todo',
      cta: t('today.claim'),
      onPress: () => {
        if (!checkedIn && !checkin.isPending) onCheckin();
      },
    });

    // 2) Predict today's matches (only when there are fixtures to predict).
    if (predicted.total > 0) {
      const allPredicted = predicted.made >= predicted.total;
      tasks.push({
        key: 'predict',
        icon: 'football',
        tint: palette.voltBright,
        title: t('today.predictTitle'),
        body: allPredicted
          ? t('today.predictAllDone', { total: n(predicted.total) })
          : t('today.predictProgress', { made: n(predicted.made), total: n(predicted.total) }),
        state: allPredicted ? 'done' : 'todo',
        cta: t('today.go'),
        onPress: () => router.push('/games/score-predict' as never),
      });
    }

    // 3) Free daily sticker pack (only when the album backend is live).
    if (albumQ.data) {
      const free = albumQ.data.free_available;
      tasks.push({
        key: 'pack',
        icon: 'albums',
        tint: palette.captain,
        title: t('today.packTitle'),
        body: free ? t('today.packAvailable') : t('today.packDone'),
        state: free ? 'todo' : 'done',
        cta: t('today.open'),
        onPress: () => router.push('/album' as never),
      });
    }

    // 4) Pick'em card of the day (only when a card is live for today).
    if (accaQ.data && accaQ.data.acca_date && accaQ.data.fixtures.length > 0) {
      const legs = accaQ.data.fixtures.length;
      const picked = accaQ.data.picks_count;
      const allPicked = picked >= legs;
      tasks.push({
        key: 'acca',
        icon: 'layers',
        tint: palette.volt,
        title: t('today.accaTitle'),
        body: allPicked
          ? t('today.accaAllDone', { total: n(legs) })
          : t('today.accaProgress', { picked: n(picked), total: n(legs) }),
        state: allPicked ? 'done' : 'todo',
        cta: t('today.go'),
        onPress: () => router.push('/games/acca' as never),
      });
    }

    // 5) Vote Player of the Match (action-only — we don't detect "voted" here).
    if (potmFixture) {
      tasks.push({
        key: 'potm',
        icon: 'star',
        tint: palette.captain,
        title: t('today.potmTitle'),
        body: t('today.potmBody'),
        state: 'action',
        cta: t('today.go'),
        onPress: () => router.push(`/match/${potmFixture.id}` as never),
      });
    }
  }

  const doneCount = tasks.filter((task) => task.state === 'done').length;
  // 'action' tasks (PotM) count toward the total but never resolve to done.
  const totalCount = tasks.length;
  const allDone = totalCount > 0 && doneCount === totalCount;
  const progress = totalCount > 0 ? doneCount / totalCount : 0;

  const reduce = useReducedMotion();
  const streak = wallet?.streak ?? 0;

  return (
    <Screen>
      {/* One-time post-onboarding nudge -> first prediction. Self-hides after shown. */}
      <FirstPredictNudge />
      <BackHeader title={t('today.title')} />
      <ScrollView contentContainerStyle={{ paddingBottom: 36, paddingTop: 4 }}>
        {/* Streak + daily progress header */}
        {userId ? (
          <Animated.View entering={reduce ? undefined : FadeInDown.springify()}>
            <Card elevated className="mx-4 mb-3 overflow-hidden px-4 py-4">
              <View className="flex-row items-center justify-between">
                <View className="flex-row items-center gap-2.5">
                  <View
                    className="h-11 w-11 items-center justify-center rounded-full"
                    style={{ backgroundColor: `${palette.volt}1F` }}
                  >
                    <Ionicons name="flame" size={22} color={palette.volt} />
                  </View>
                  <View>
                    <AppText weight="bold" tabular className="text-ink" style={{ fontSize: 20 }}>
                      {streak > 0 ? t('today.streakFlame', { count: n(streak) }) : t('today.streakNone')}
                    </AppText>
                    <AppText variant="micro" className="text-muted">
                      {totalCount > 0
                        ? t('today.progressDone', { done: n(doneCount), total: n(totalCount) })
                        : t('today.checklist')}
                    </AppText>
                  </View>
                </View>

                {/* Coins, kept glanceable on the right. */}
                <View className="items-end">
                  <View className="flex-row items-center gap-1">
                    <Ionicons name="server" size={14} color={palette.captain} />
                    <AppText weight="bold" tabular className="text-ink" style={{ fontSize: 16 }}>
                      {n(wallet?.coins ?? 0)}
                    </AppText>
                  </View>
                  <AppText variant="micro" className="text-muted">
                    {t('today.coins')}
                  </AppText>
                </View>
              </View>

              {/* Thin progress track that fills as tasks are completed. */}
              {totalCount > 0 ? (
                <View
                  className="mt-3.5 h-1.5 overflow-hidden rounded-full"
                  style={{ backgroundColor: palette.line }}
                  accessibilityRole="progressbar"
                  accessibilityValue={{ min: 0, max: totalCount, now: doneCount }}
                >
                  <View
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(progress * 100, progress > 0 ? 8 : 0)}%`,
                      backgroundColor: allDone ? palette.win : palette.volt,
                      alignSelf: isRTL ? 'flex-end' : 'flex-start',
                    }}
                  />
                </View>
              ) : null}
            </Card>
          </Animated.View>
        ) : null}

        {userId && levelQ.data ? (
          <Card className="mx-4 mb-3 px-4 py-3">
            <LevelBar level={levelQ.data} />
          </Card>
        ) : null}

        {/* Today's checklist */}
        {userId ? (
          <>
            <AppText variant="caption" weight="bold" className="mx-4 mb-2 mt-1 uppercase tracking-wide text-soft">
              {t('today.checklist')}
            </AppText>

            {allDone ? (
              <Animated.View entering={reduce ? undefined : FadeInDown.springify()}>
                <Card
                  className="mx-4 mb-2.5 items-center gap-1.5 px-4 py-5"
                  style={{ borderColor: palette.voltLine }}
                >
                  <View
                    className="h-12 w-12 items-center justify-center rounded-full"
                    style={{ backgroundColor: `${palette.win}26` }}
                  >
                    <Ionicons name="checkmark-done" size={26} color={palette.win} />
                  </View>
                  <AppText variant="heading" weight="bold" className="text-ink">
                    {t('today.allDoneTitle')}
                  </AppText>
                  <AppText variant="micro" className="px-4 text-center leading-4 text-muted">
                    {t('today.allDoneBody')}
                  </AppText>
                </Card>
              </Animated.View>
            ) : null}

            {tasks.map((task, i) => (
              <TaskRow key={task.key} task={task} index={i} />
            ))}
          </>
        ) : null}

        {/* Today's matches */}
        <View className="mx-4 mb-2 mt-1 flex-row items-center justify-between">
          <AppText variant="caption" weight="bold" className="uppercase tracking-wide text-soft">
            {t('today.matches')}
          </AppText>
          {live.length > 0 ? (
            <View className="flex-row items-center gap-1 rounded-full px-2 py-0.5" style={{ backgroundColor: `${palette.live}22` }}>
              <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: palette.live }} />
              <AppText variant="micro" weight="bold" tabular style={{ color: palette.live }}>
                {t('today.liveCount', { count: n(live.length) })}
              </AppText>
            </View>
          ) : null}
        </View>

        {fixturesQ.isPending ? (
          <View className="gap-2 px-4">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </View>
        ) : ordered.length === 0 ? (
          <EmptyState icon="calendar-outline" title={t('today.noMatchesTitle')} body={t('today.noMatchesBody')} />
        ) : (
          <View className="mb-3">
            {ordered.map((f) => (
              <FixtureRow key={f.id} fixture={f} />
            ))}
          </View>
        )}

        {/* Launchpad: deeper jumps into the rest of the app */}
        <AppText variant="caption" weight="bold" className="mx-4 mb-2 mt-1 uppercase tracking-wide text-soft">
          {t('today.forYou')}
        </AppText>

        <LinkCard
          icon="checkmark-done-circle"
          tint={palette.win}
          title={t('today.missionsTitle')}
          body={
            !userId
              ? t('today.signInMissions')
              : (() => {
                  const missions = missionsQ.data ?? [];
                  const missionsDone = missions.filter((m) => m.completed).length;
                  const claimable = missions
                    .filter((m) => !m.completed)
                    .reduce((s, m) => s + m.reward_coins, 0);
                  return claimable > 0
                    ? t('today.missionsSummary', {
                        done: n(missionsDone),
                        total: n(missions.length),
                        coins: n(claimable),
                      })
                    : t('today.missionsAllDone', { total: n(missions.length) });
                })()
          }
          onPress={() => router.push('/rewards' as never)}
        />

        <LinkCard
          icon="newspaper"
          tint={palette.voltBright}
          title={t('today.newsTitle')}
          body={t('today.newsBody')}
          onPress={() => router.push('/news' as never)}
        />

        <LinkCard
          icon="game-controller"
          tint={palette.volt}
          title={t('today.sportsTitle')}
          body={t('today.sportsBody')}
          onPress={() => router.push('/sports' as never)}
        />

        <LinkCard
          icon="trophy"
          tint={palette.captain}
          title={t('today.wcGroupsTitle')}
          body={t('today.wcGroupsBody')}
          onPress={() => router.push('/wc-groups' as never)}
        />

        {/* World Cup context: past finals. Self-hides until history is synced. */}
        <View className="mt-4">
          <WcHistory />
        </View>
      </ScrollView>
    </Screen>
  );
}
